import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { STATUSES } from "@/lib/api";
import AddressAutocomplete from "@/components/AddressAutocomplete";

const SOURCES = ["manual", "website", "referral", "google", "facebook", "sms_campaign", "email_campaign", "direct_mail", "yelp", "other"];

const empty = {
  customer_name: "",
  property_address: "",
  latitude: null,
  longitude: null,
  phone: "",
  email: "",
  lead_source: "manual",
  service_requested: "",
  estimated_value: 0,
  status: "new_lead",
  notes: "",
  follow_up_date: "",
  campaign_id: "",
};

export default function LeadFormDialog({ open, onOpenChange, onSubmit, initial, campaigns = [] }) {
  const [form, setForm] = useState(empty);

  useEffect(() => {
    if (open) setForm({ ...empty, ...(initial || {}) });
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, estimated_value: Number(form.estimated_value) || 0 };
    if (!payload.campaign_id) delete payload.campaign_id;
    if (!payload.follow_up_date) delete payload.follow_up_date;
    await onSubmit(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="lead-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{initial?.id ? "Edit Lead" : "New Lead"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label>Customer name *</Label>
            <Input required data-testid="lead-form-customer-name" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} />
          </div>
          <div>
            <Label>Phone</Label>
            <Input data-testid="lead-form-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" data-testid="lead-form-email" value={form.email} onChange={(e) => set("email", e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <Label>Property address</Label>
            <AddressAutocomplete
              data-testid="lead-form-address"
              value={form.property_address}
              onChange={(v) => set("property_address", v)}
              onSelect={(p) => setForm((f) => ({
                ...f,
                property_address: p.formatted,
                latitude: p.lat,
                longitude: p.lng,
              }))}
            />
          </div>
          <div>
            <Label>Service requested</Label>
            <Input data-testid="lead-form-service" value={form.service_requested} onChange={(e) => set("service_requested", e.target.value)} />
          </div>
          <div>
            <Label>Estimated value ($)</Label>
            <Input type="number" data-testid="lead-form-value" value={form.estimated_value} onChange={(e) => set("estimated_value", e.target.value)} />
          </div>
          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="lead-form-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Lead source</Label>
            <Select value={form.lead_source} onValueChange={(v) => set("lead_source", v)}>
              <SelectTrigger data-testid="lead-form-source"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SOURCES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Follow-up date</Label>
            <Input type="date" data-testid="lead-form-followup" value={(form.follow_up_date || "").slice(0, 10)} onChange={(e) => set("follow_up_date", e.target.value)} />
          </div>
          <div>
            <Label>Attribution campaign</Label>
            <Select value={form.campaign_id || "none"} onValueChange={(v) => set("campaign_id", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="lead-form-campaign"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.channel}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label>Notes</Label>
            <Textarea rows={3} data-testid="lead-form-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} />
          </div>
          <DialogFooter className="md:col-span-2 mt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="lead-form-cancel">Cancel</Button>
            <Button type="submit" className="bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift" data-testid="lead-form-save">
              {initial?.id ? "Save changes" : "Create lead"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
